import * as React from 'react';

import classNames from 'classnames';

import {CouponPill} from 'shells/billing/billing-summary/coupon-pill';
import type {BillingRoute} from 'shells/billing/types';
import {SaveButton} from 'shells/button/save-button/save-button';
import {CardIcon} from 'shells/icon/svg/card-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {FooterBar} from 'shells/modal/footer-bar';
import {Body} from 'shells/typography/body';

import type {GetAccountBilling_accountBilling_currentPlan_crm_coupon as Coupon} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';

import {ManageBreadcrumbs} from './manage-breadcrumbs';

import styles from './manage.module.css';

type Props = {
    accountCoupons: Coupon[];
    route: BillingRoute;
    dueCost: string | null | undefined;
    planDetails: string | null | undefined;
    engagementDetails: string | null | undefined;
    marketingDetails?: string | null | undefined;
    isLoading: boolean;
    isButtonDisabled: boolean;
    isSaving: boolean;
    buttonTooltip: string;
    onNext: () => void;
    hasUnsavedBillingChanges: boolean;
};

export function Footer(props: Props) {
    const buttonText = props.route === 'review' ? 'Complete purchase' : 'Continue';

    const {user} = useGetSessionUser();

    const isMainSidebarCollapsed = user && user.isMainSidebarCollapsed;
    const isAiAgentSidebarOpen = user && user.isAiAgentSidebarOpen;

    const containerStyleNames = classNames(styles.footer, {
        [styles['main-sidebar-collapsed']]: isMainSidebarCollapsed && !isAiAgentSidebarOpen,
        [styles['ai-agent-sidebar-open']]: isAiAgentSidebarOpen && !isMainSidebarCollapsed,
        [styles['main-closed-agent-open']]: isMainSidebarCollapsed && isAiAgentSidebarOpen,
    });

    const infoStyleNames = classNames({
        [styles.info]: !isAiAgentSidebarOpen,
        [styles['info-agent-open']]: isAiAgentSidebarOpen,
    });

    const coupon = props.accountCoupons.length > 0 ? props.accountCoupons[0] : null;

    return (
        <div className={containerStyleNames}>
            <FooterBar
                left={
                    <div className={styles['breadcrumbs-container']}>
                        <ManageBreadcrumbs
                            route={props.route}
                            planDetails={props.planDetails}
                            engagementDetails={props.engagementDetails}
                            marketingDetails={props.marketingDetails}
                            // @ts-expect-error
                            isButtonDisabled={props.isButtonDisabled}
                            hasUnsavedBillingChanges={props.hasUnsavedBillingChanges}
                        />
                    </div>
                }
                right={
                    <StackLegacy spacing={16} alignCenter={true} isHorizontal={true}>
                        <div
                            className={classNames(
                                'flex align-end flex-dir-col gap-4',
                                infoStyleNames
                            )}
                        >
                            <div className='flex align-center gap-8'>
                                {props.isLoading && (
                                    <>
                                        <CardIcon size={18} alignCenter={true} />
                                        <Body color='light'>Calculating your monthly total…</Body>
                                    </>
                                )}
                                {props.dueCost && !props.isLoading && (
                                    <>
                                        <CardIcon size={18} alignCenter={true} />
                                        <Body>
                                            You’ll be charged{' '}
                                            <Body isBold={true}>{props.dueCost}</Body> next billing
                                            cycle
                                        </Body>
                                    </>
                                )}
                            </div>
                            {/* @ts-expect-error */}
                            {coupon && <CouponPill coupon={coupon} code={coupon.code} />}
                        </div>
                        <SaveButton
                            isSaving={props.isSaving}
                            // @ts-expect-error
                            onClick={props.onNext}
                            variant='save'
                            size='big'
                            disabled={props.isButtonDisabled}
                            buttonText={buttonText}
                            savingText='Submitting'
                            tooltipText={props.buttonTooltip}
                        />
                    </StackLegacy>
                }
                noPadding={true}
                noMargin={true}
            />
        </div>
    );
}
